import React, { useState, useEffect } from "react";
import { Check, LogOut, Sparkles, Award, ArrowRight } from "lucide-react";
import { GOLD } from "../constants/theme";
import { BAM_LOGO_PNG } from "../constants/branding";
import { supabase } from "../../supabaseClient";
import { logEvent, FunnelEvent } from "../utils/analytics";
import { getMetaContext } from "../../utils/metaPixel";
import { getStoredRef } from "../utils/affiliateRef";
import { isNative } from "../../utils/platform";
import { assertNotStaging } from "../../config/environment";
import { PLATFORM_PRICES } from "../film/afsPlans";

const PLANS = {
  monthly: { price: "price_1TMbhjG8VcQAE5BpfN0LhHFe", amount: "34.99", label: "/mo", period: "monthly" },
  annual:  { price: "price_1TMbiEG8VcQAE5BpSBKc4BoQ", amount: "349.99", label: "/yr", period: "annual", save: "2 months free" },
  // Coaches on a paid Advantage Flow plan (2026-10-28): the rest of the platform for $19.99 a month while it runs.
  afs:     { price: PLATFORM_PRICES.afs, amount: "19.99", label: "/mo", period: "monthly" },
};

export function Paywall({ userEmail, userId, onSignOut, hasCert, onOpenCert, afsOffer = false }) {
  // Annual leads. Only ~10% of members are on it, and monthly churns at
  // 15-21% a month, so every coach who starts annual is a year of renewal
  // risk removed. $349.99 against 12 x $34.99 is $69.89, which is 1.997
  // months: "2 months free" is the same number as "save ~$70" and is the
  // version people can picture.
  const [plan, setPlan] = useState(afsOffer ? "afs" : "annual");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // True when the user just landed back here via Stripe's cancel_url
  // (e.g. they redeemed a promo code but bailed on the card form, or
  // the code was rejected). Surface a tailored explainer so they don't
  // think the platform is broken.
  const [cancelled, setCancelled] = useState(false);

  // Founding 50 — first 50 platform subscribers get 15% off forever
  // via a Stripe coupon auto-applied at checkout. Mirror the live
  // remaining count from /api/founding-spots so the paywall price
  // matches what coaches actually pay (avoids a "$34.99 → surprise $29.74"
  // moment at Stripe Checkout).
  const [founding, setFounding] = useState(null);
  useEffect(() => {
    let aborted = false;
    fetch("/api/founding-spots", { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!aborted && d) setFounding(d); })
      .catch(() => {});
    return () => { aborted = true; };
  }, []);

  // Early User Family Program: users invited via ?invited=1 (typically
  // existing certified coaches) get 90 days free + 50% off forever via
  // a Stripe coupon attached at checkout. Persisted to sessionStorage
  // so the flag survives the Stripe round-trip on a cancel/return.
  const isEarlyUser = (() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get("invited") === "1") {
        sessionStorage.setItem("bam_early_user", "1");
        return true;
      }
      return sessionStorage.getItem("bam_early_user") === "1";
    } catch { return false; }
  })();

  useEffect(() => {
    logEvent(FunnelEvent.PaywallShown, { earlyUser: isEarlyUser });
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get("checkout") === "cancelled") {
        logEvent(FunnelEvent.CheckoutCancelled);
        setCancelled(true);
      }
    } catch {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubscribe = async () => {
    setLoading(true);
    setError(null);
    logEvent(FunnelEvent.CheckoutStarted, { plan });
    try {
      assertNotStaging("Checkout");
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) throw new Error("Not signed in \u2014 please refresh and try again.");

      const checkoutUrl =
        process.env.REACT_APP_CHECKOUT_URL ||
        "https://rlfekxctjyvozujzbzwm.supabase.co/functions/v1/create-checkout-session";

      const res = await fetch(checkoutUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          userId,
          email: userEmail,
          priceId: PLANS[plan].price,
          // Early User Family Program flag — backend applies 90-day
          // trial + 50%-off-forever coupon when true.
          earlyUser: isEarlyUser && !afsOffer,
          // Affiliate ref (?ref=<code>) → backend applies a flat 10% coupon
          // (if no better discount) and credits the coach for attribution.
          ref: getStoredRef(),
          // Meta attribution, carried to the server through Stripe.
          //
          // The Conversions API Purchase is fired from the Stripe
          // webhook, which is a server-to-server call from Stripe and
          // therefore has none of this: no cookies, no user agent, and
          // an IP belonging to Stripe. It was sending a hashed email
          // and nothing else, which is why match quality sat at 3.2.
          //
          // So the browser hands it over at checkout time, the Edge
          // Function parks it on the session metadata, and the webhook
          // reads it back when the payment lands.
          meta: getMetaContext(),
          // Query params go BEFORE the hash — HashRouter ignores them (router
          // just cares about the hash) and window.location.search can read
          // them. If the query went AFTER the hash (inside hash fragment),
          // it'd be invisible to URLSearchParams(location.search).
          // plan_value rides along so the browser Purchase reports what
          // was actually bought. It used to hardcode 34.99, which made
          // every annual subscription look like a monthly one. The CAPI
          // event takes the true figure from Stripe; this is the
          // browser's best guess at the same number, and a guess of the
          // right order beats a wrong constant.
          successUrl: `${window.location.origin}/?checkout=success&session_id={CHECKOUT_SESSION_ID}&plan_value=${PLANS[plan].amount}${isEarlyUser ? "&invited=1" : ""}#/platform`,
          cancelUrl: `${window.location.origin}/?checkout=cancelled${isEarlyUser ? "&invited=1" : ""}#/paywall`,
        }),
      });

      if (!res.ok) {
        // Log the raw backend text for debugging, but never render it —
        // a coach on a failed checkout shouldn't see server error dumps.
        const text = await res.text().catch(() => "");
        console.error("[Paywall] checkout backend error:", res.status, text);
        throw new Error("friendly");
      }

      const payload = await res.json();
      const url = payload?.url;
      if (!url) { console.error("[Paywall] no checkout URL in payload", payload); throw new Error("friendly"); }
      window.location.href = url;
    } catch (e) {
      console.error("[Paywall] Stripe checkout failed:", e);
 setError("Couldn't start checkout. Please try again in a moment: if it keeps happening, email support@byanymeanscoaches.com.");
      setLoading(false);
    }
  };

  const features = [
    "600+ drills, games & coaching insights",
    "Training plan & playbook builders",
    "Save your own drills to a personal library",
    "AI coaching assistant (Ask BAM)",
    "Global coach community",
    "Weekly live coaching calls",
    "New content every week",
  ];

  const selected = PLANS[plan];
  const isAnnual = plan === "annual";

  // Founding-50 applies on top of monthly OR annual (Stripe handles the
  // attach), but NOT when the user is in the EUFP flow (50% > 15%, EUFP
  // wins). Discounted amount is computed locally for display only —
  // Stripe is the source of truth at checkout.
  const showFounding = !afsOffer && !isEarlyUser && founding?.valid && founding.remaining > 0;
  const baseAmount = parseFloat(selected.amount);
  const discountedAmount = showFounding
    ? (baseAmount * 0.85).toFixed(2)
    : selected.amount;

  // Defensive: never show Stripe pricing / external checkout inside the
  // native iOS app (Apple reader-app rules). Native users are routed to
  // NativeSubscriptionGate by the caller; this guarantees Paywall can't leak
  // even if it's ever mounted directly. Placed after all hooks so hook order
  // is unconditional.
  if (isNative()) return null;

  return (
    <div style={{
      position: "fixed", inset: 0, background: "#050505", color: "#fff",
      fontFamily: "'DM Sans', sans-serif", display: "flex", alignItems: "center",
      justifyContent: "center", padding: 20, overflow: "auto",
    }}>
      <div aria-hidden="true" style={{
        position: "absolute", inset: 0, pointerEvents: "none",
        background: "radial-gradient(ellipse 800px 500px at 50% 30%, rgba(226,221,159,0.08), transparent 70%)",
      }} />

      <button onClick={onSignOut} aria-label="Sign out"
        style={{
          position: "fixed", top: 20, right: 20, zIndex: 10,
          background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)",
          color: "#888", padding: "8px 14px", borderRadius: 8, fontSize: 12, fontWeight: 600,
          cursor: "pointer", display: "flex", alignItems: "center", gap: 6, fontFamily: "inherit",
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.08)"; e.currentTarget.style.color = "#fff"; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.04)"; e.currentTarget.style.color = "#888"; }}>
        <LogOut size={12} /> Sign out
      </button>

      <div style={{
        position: "relative", width: "100%", maxWidth: 460,
        background: "linear-gradient(180deg, #131313, #0E0E0E)",
        border: `1px solid rgba(226,221,159,0.18)`, borderRadius: 18,
        padding: "32px 36px 24px",
        boxShadow: "0 40px 120px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.03)",
        textAlign: "center",
      }}>
        {/* Logo — single, centered, no redundant "Subscription required" badge
            (the title already says it). */}
        <img src={BAM_LOGO_PNG} alt="BAM" style={{
          width: 40, height: 40, objectFit: "contain",
          filter: "drop-shadow(0 0 16px rgba(226,221,159,0.3))",
          marginBottom: 14,
        }} />

        {/* Early User Family Program banner — visible when the user
            arrived via ?invited=1 so they know the perk is being applied
            before they click subscribe. */}
        {isEarlyUser && (
          <div style={{
            margin: "0 auto 14px", padding: "10px 14px", maxWidth: 380,
            background: `linear-gradient(135deg, ${GOLD}1f, ${GOLD}08)`,
            border: `1px solid ${GOLD}55`, borderRadius: 10,
            fontSize: 11.5, lineHeight: 1.5, color: "#e8e8e8", textAlign: "left",
          }}>
            <div style={{ fontSize: 9.5, fontWeight: 800, color: GOLD, letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 4 }}>
              ✨ Early User Family Program
            </div>
            Your invite unlocks <strong style={{ color: GOLD }}>90 days free</strong>, then <strong style={{ color: GOLD }}>50% off for life</strong>. Applied automatically at checkout.
          </div>
        )}

        <h1 style={{
          fontFamily: "'Bebas Neue', sans-serif",
          fontSize: 30, letterSpacing: 1.4,
          margin: "0 0 6px", lineHeight: 1.05, color: "#fff",
        }}>
          UNLOCK THE PLATFORM
        </h1>
        <p style={{ color: "#9a9a9a", fontSize: 12, margin: "0 0 18px", lineHeight: 1.5 }}>
          Signed in as <span style={{ color: "#e8e8e8", fontWeight: 600 }}>{userEmail}</span>
        </p>

        {/* A coach who owns the certification but not the platform lands here
            on every platform route, which is intended. What was not intended
            is that this screen had no way into the certification, so the only
            door to a thing they had paid for was the link in their receipt.
            Shown above the pricing because they are not here to buy. */}
        {hasCert && (
          <div style={{
            margin: "0 0 16px", padding: "12px 14px",
            background: `linear-gradient(135deg, ${GOLD}1c, ${GOLD}08)`,
            border: `1px solid ${GOLD}55`, borderRadius: 10, textAlign: "left",
          }}>
            <div style={{
              display: "flex", alignItems: "center", gap: 6, marginBottom: 6,
              fontSize: 9.5, fontWeight: 800, color: GOLD,
              letterSpacing: 1.2, textTransform: "uppercase",
            }}>
              <Award size={12} /> You own the certification
            </div>
            <div style={{ fontSize: 11.5, lineHeight: 1.5, color: "#e8e8e8", marginBottom: 10 }}>
              That is yours to keep and does not need a subscription. The
              subscription below is for the coaching platform, which is
              separate.
            </div>
            <button onClick={onOpenCert}
              style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                padding: "9px 15px", borderRadius: 9, border: "none",
                background: GOLD, color: "#111", fontSize: 12.5, fontWeight: 800,
                cursor: "pointer", fontFamily: "inherit",
              }}>
              Go to my certification <ArrowRight size={13} />
            </button>
          </div>
        )}

        {cancelled && (
          <div role="status" style={{
            marginBottom: 14,
            padding: "10px 12px",
            background: "rgba(226,221,159,0.07)",
            border: `1px solid ${GOLD}33`,
            borderRadius: 8,
            fontSize: 11.5, lineHeight: 1.5,
            color: "#cfcfcf",
            textAlign: "left",
          }}>
            <div style={{ color: GOLD, fontWeight: 700, fontSize: 10.5, letterSpacing: 1, textTransform: "uppercase", marginBottom: 4 }}>
              Checkout cancelled
            </div>
            Click Subscribe to try again. If a promo code didn't work, double-check it's the public Promotion Code (not a coupon ID) and that it's still active.
          </div>
        )}

        {/* On Advantage Flow (2026-10-28, Coleman: "they only get access to PDPs and practice planning... and they have
            to pay an extra $19.99 for the rest"): what they already have, and the one price, no toggle. */}
        {afsOffer && (
          <div style={{
            margin: "0 0 16px", padding: "12px 14px", textAlign: "left",
            background: `linear-gradient(135deg, ${GOLD}1c, ${GOLD}08)`,
            border: `1px solid ${GOLD}55`, borderRadius: 10,
          }}>
            <div style={{ fontSize: 9.5, fontWeight: 800, color: GOLD, letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 6 }}>
              For Advantage Flow coaches
            </div>
            <div style={{ fontSize: 11.5, lineHeight: 1.5, color: "#e8e8e8", marginBottom: 10 }}>
              Player development plans and practice planning already come with your plan. The rest of the platform is
              $19.99 a month while your Advantage Flow plan runs, then the regular monthly price.
            </div>
            <a href="#/afs" style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12.5, fontWeight: 800, color: GOLD, textDecoration: "none" }}>
              Back to Advantage Flow <ArrowRight size={13} />
            </a>
          </div>
        )}

        {/* Plan Toggle */}
        {!afsOffer && <div style={{
          display: "flex", background: "rgba(255,255,255,0.04)", borderRadius: 10, padding: 3,
          marginBottom: 16, border: "1px solid rgba(255,255,255,0.06)",
        }}>
          {/* Annual first as well as selected. A toggle reads left to
              right, and the option on the left is the one people treat as
              the normal choice. */}
          {[["annual", "Annual"], ["monthly", "Monthly"]].map(([key, label]) => (
            <div key={key} onClick={() => setPlan(key)}
              style={{
                flex: 1, textAlign: "center", padding: "10px 0", borderRadius: 8, cursor: "pointer",
                background: plan === key ? `${GOLD}18` : "transparent",
                border: plan === key ? `1px solid ${GOLD}40` : "1px solid transparent",
                transition: "all .15s",
              }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: plan === key ? GOLD : "#888" }}>{label}</div>
              {key === "annual" && (
                <div style={{ fontSize: 10, color: "#5AB584", fontWeight: 700, marginTop: 2 }}>2 months free</div>
              )}
            </div>
          ))}
        </div>}

        {/* Price — balanced rhythm: $ matches digit color/weight, period
            is small gold, "cancel anytime" drops below as a secondary line. */}
        <div style={{
          padding: "14px 0 12px",
          borderTop: "1px solid rgba(255,255,255,0.06)",
          borderBottom: "1px solid rgba(255,255,255,0.06)",
          marginBottom: 16,
        }}>
          {showFounding && (
            <div style={{ fontSize: 13, color: "#888", marginBottom: 4, textDecoration: "line-through", letterSpacing: .3 }}>
              ${selected.amount}{selected.label}
            </div>
          )}
          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "center", gap: 2 }}>
            <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 30, color: GOLD, lineHeight: 1, marginRight: 1 }}>$</span>
            <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 44, color: GOLD, letterSpacing: 0, lineHeight: 1 }}>
              {discountedAmount}
            </span>
            <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 16, color: GOLD, letterSpacing: .5, marginLeft: 4, opacity: .85 }}>
              {selected.label}
            </span>
          </div>
          <div style={{ fontSize: 11, color: "#888", marginTop: 5, letterSpacing: .3 }}>
            cancel anytime
          </div>
          {showFounding && (
            <div style={{ fontSize: 10.5, fontWeight: 700, color: GOLD, marginTop: 5, letterSpacing: 1.1, textTransform: "uppercase" }}>
              Founding 50 · 15% off forever · {founding.remaining} of {founding.total} left
            </div>
          )}
          {isAnnual && !showFounding && (
            <div style={{ fontSize: 11, color: "#5AB584", fontWeight: 600, marginTop: 4 }}>
 That's ${(349.99 / 12).toFixed(2)}/mo: best value
            </div>
          )}
          {isAnnual && showFounding && (
            <div style={{ fontSize: 11, color: "#5AB584", fontWeight: 600, marginTop: 4 }}>
 That's ${(parseFloat(discountedAmount) / 12).toFixed(2)}/mo: best value
            </div>
          )}
        </div>

        {/* Features */}
        <div style={{ marginBottom: 14, textAlign: "left" }}>
          {features.map((f) => (
            <div key={f} style={{ display: "flex", alignItems: "center", gap: 10, padding: "4px 0", fontSize: 12.5, color: "#d8d8d8" }}>
              <div style={{ width: 16, height: 16, borderRadius: "50%", background: `${GOLD}14`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <Check size={10} color={GOLD} strokeWidth={3} />
              </div>
              {f}
            </div>
          ))}
        </div>

        {/* CTA */}
        <button onClick={handleSubscribe} disabled={loading}
          style={{
            width: "100%", padding: "12px 24px",
            background: `linear-gradient(180deg, ${GOLD}, #d4ce8a)`,
            color: "#0A0A0A", border: "none", borderRadius: 10,
            fontSize: 13, fontWeight: 800, letterSpacing: 1.5,
            fontFamily: "'Bebas Neue', sans-serif", textTransform: "uppercase",
            cursor: loading ? "wait" : "pointer", opacity: loading ? 0.7 : 1,
            boxShadow: `0 10px 40px ${GOLD}28, inset 0 0 0 1px rgba(255,255,255,0.15)`,
          }}>
          {loading ? "Redirecting to Stripe..." : `Subscribe ${isAnnual ? "Annually" : "Monthly"}`}
        </button>

        {error && (
          <div role="alert" style={{
            marginTop: 10, padding: "9px 12px", background: "rgba(224,96,96,0.08)",
            border: "1px solid rgba(224,96,96,0.25)", borderRadius: 8,
            fontSize: 11.5, color: "#ff9d9d", lineHeight: 1.5, textAlign: "left",
          }}>
            {error}
          </div>
        )}

        <div style={{
          marginTop: 12, padding: "8px 12px", background: "rgba(226,221,159,0.04)",
          border: "1px solid rgba(226,221,159,0.10)", borderRadius: 8,
          fontSize: 10.5, color: "#9a9a9a", display: "flex", alignItems: "center",
          gap: 6, justifyContent: "center", lineHeight: 1.4,
        }}>
          <Sparkles size={11} color={GOLD} />
          <span><span style={{ color: GOLD, fontWeight: 700 }}>BAM Certification</span> sold separately inside the platform</span>
        </div>

        <div style={{ fontSize: 10, color: "#555", marginTop: 10, lineHeight: 1.5, letterSpacing: 0.2 }}>
          Secure payment via Stripe · <a href="mailto:support@byanymeanscoaches.com" style={{ color: "#777", textDecoration: "none" }}>support@byanymeanscoaches.com</a>
        </div>
      </div>
    </div>
  );
}
